import { Component, h, State, Prop, Element, Watch, Method, Event, EventEmitter } from '@stencil/core';
import '../../../stories/Components/MessageBubble/DocumentMessageBubble/DocumentMessageBubble';
import '../../../stories/Components/MessageBubble/ImageMessageBubble/ImageMessageBubble';
import '../../../stories/Components/MessageBubble/VideoMessageBubble/VideoMessageBubble';
import '../../../stories/Components/MessageBubble/IncomingTextMessageBubble/IncomingTextMessageBubble';
import '../../../stories/Components/MessageBubble/OutgoingTextMessageBubble/OutgoingTextMessageBubble';
import { getDeviceID, getGroupcastUrlQuery } from '../../../lib/helpers';
import { formatMessageDateFromTimestamp } from '../../../utils/getFomattedDate';
import '../../../stories/Components/MessageDatePill/MessageDatePill';
import '../../../stories/Components/Loader/Spinner/Spinner';
import ChatSDK from 'test-iso-chat-sdk';
import '../../../stories/Components/ReplyBlock/ReplyBlock';
import '../../../stories/Components/ReplyMessageContainer/ReplyMessageContainer';
import { formatUnixTimestamp } from '../../../utils/unixTimeConverter.js';
import '../../../stories/Components/SearchableList/SearchableList';
import '../../../stories/Components/ForwardMessage/ForwardMessageContainer';
import '../../../stories/Components/MessageInfo/MessageInfo';
import NoMessagePlaceholder from '../../../assets/svg/NoMessagePlaceholder.svg';
import '../../../stories/Components/MessageBubble/LinkMessageBubble/LinkMessageBubble';
import { WEB_CONTENT } from '../../../constants/content';
import ProfilePlaceholder from '../../../assets/images/avatar.svg';
import { postReaction, deleteReaction } from '../../../api/message-reaction.js';
import '../../ChatUIComponent/AudioBubble';
import { drawerDialogService } from '../../../stories/Components/Dialog-Drawer/dialog-drawer.js';
// import { ChatBg } from '../../../constants/chat-assets.js';
import { deleteGroupcastMessageSelf, deleteGroupcastMessageEveryone, getGroupcastMessageReadStatus, getGroupcastMessageDeliveryStatus } from '../../../api/groupcast.js';

@Component({
  tag: 'web-chat-body',
  shadow: true,
  styleUrl: 'web-chat-body.css',
})
class WebChatBody {
  //props
  @Prop() skip: number;
  @Prop() allMessages: any;
  @Prop() userMetaData: any;
  @Prop() isMessagesLoading: boolean;
  @Prop() isMessageSending: boolean;
  @Prop() isMessageLimitEnd: boolean;
  @Prop() updateSkip: () => void;
  @Prop() isScrollToBottom: boolean;
  @Prop() isScrollToTop: boolean;
  @Prop() deleteForAll: ({ conversationId, messageIds }) => void;
  @Prop() deleteForSelf: ({ conversationId, messageIds }) => void;
  @Prop() changeScrollToBottomState: (newState: boolean) => void;
  @Prop() changeScrollToTopState: (newState: boolean) => void;
  @Prop() updateAllMessages: (newMessages: any) => void;
  @Prop() isChatClosed: boolean;
  @Prop() isChatMinimized: boolean;
  @Prop() selectedConversationId: string;
  @Prop() allAttachments: any;
  @Prop() highlightedMessageId: string | null;
  @Prop() onMediaClick: () => void;
  @Prop() conversationList: any = [];
  @Prop() conversationDetails: any = {};
  @Prop() newMessageNotification: { messageId: string; count: number; conversationId: string } | null = null;
  @Prop() loadingMediaGallery: boolean;

  //states
  @State() previousTotalScrollHeight: number = 0;
  @State() deviceId: string = '';
  @State() scrollTimeout: any = null;
  @State() isDeletePopupOpen: String | null = null;
  @State() isMessageActionOpen: String | null = null;
  @State() deletedMessages: Set<string> = new Set();
  @State() isTabActive: boolean = true;
  @State() isForwardPopupOpen: String | null = null;
  @State() isSelf: Boolean = false;
  @State() isReplyBlockOpen: string | null = null;
  @State() messageActionPopupElement: HTMLElement;
  @State() popupRef: HTMLElement;
  @State() messageInfoOpen: boolean = false;
  @State() messagePreviewText: string = '';
  @State() messageDate: string = '';
  @State() messageDeliveryTime: number = 0;
  @State() messageReadTime: number = 0;
  @State() showScrollButton: boolean = true;
  @State() isScrolling: boolean = false;
  @State() unreadMessageCount: number = 0;
  @State() isChatBodyUnscrollable: boolean = false;
  @State() selectedMessage: any = {};
  @State() readUserList: any[] = [];
  @State() deliveryUserList: any[] = [];
  @State() pendingDeliveryCount: number = 0;
  @State() pendingReadCount: number = 0;
  @State() isLoadingInfo: Boolean = true;
  //others
  @Element() el: HTMLElement;
  @Event() replyMessageDetails: EventEmitter;
  @Event() newMessageCountReset: EventEmitter;
  @Event() messageReadStatusUpdated: EventEmitter;
  private extractUrls(text: string): string[] {
    if (!text) return [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  }

  isScrolledToBottom = () => {
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    if (chatBodyDiv) {
      const threshold = 200; // pixels from bottom to consider "at bottom"
      return chatBodyDiv.scrollHeight - chatBodyDiv.scrollTop - chatBodyDiv.clientHeight < threshold;
    }
    return true;
  };

  openForwardPopup = async (event: Event, messageId: string, message: any) => {
    event.stopPropagation();
    drawerDialogService.openDialog('forward-memebers', {
      allMessages: this.allMessages,
      conversationList: this.conversationList,
      deviceId: this.deviceId,
      messageId,
      message,
    });
  };
  getReactionEmoji(reactionType: String) {
    const emojiMap = {
      star: '⭐',
      crying_with_laughter: '😂',
      yes: '👍',
      no: '❌',
      party: '🥳',
      high_five: '✋',
      rock: '🪨',
      facepal: '🤦',
      sarcastic: '🙄',
      talking_too_much: '🤫',
      crying: '😭',
      heart: '❤️',
      surprised: '😮',
      bowing: '🙇',
    };
    return emojiMap[reactionType.toLowerCase()] || reactionType;
  }
  openDeletePopup = (event: Event, messageId: string, isSelf: boolean) => {
    event.stopPropagation();
    drawerDialogService.openDialog('delete-message', {
      deleteMessageForEveryone: () => this.deleteMessageForEveryone(messageId),
      deleteMessageForSelf: () => this.deleteMessageForSelf(messageId),
      isSelf: isSelf,
    });
  };

  openMessageActionsPopup = (event: Event, messageId: string) => {
    event.stopPropagation();

    // If clicking on already open popup, close it
    if (this.isMessageActionOpen === messageId) {
      this.isMessageActionOpen = null;
      return;
    }
    this.isMessageActionOpen = messageId;

    const messageElement = this.el.shadowRoot.querySelector(`[data-message-id="${messageId}"]`);
    if (!messageElement) return;

    const kebabMenu = messageElement.querySelector('.kebab-menu') as HTMLElement;
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body') as HTMLElement;
    if (!kebabMenu || !chatBodyDiv) return;
    // Create an observer to wait for the popup to be added
    const observer = new MutationObserver((_, observerInstance) => {
      const popup = messageElement.querySelector('.messageActionPopup') as HTMLElement;
      if (popup) {
        // Once found, disconnect the observer
        observerInstance.disconnect();

        // Calculate available space and apply positioning classes using toggle
        const kebabRect = kebabMenu.getBoundingClientRect();
        const chatBodyRect = chatBodyDiv.getBoundingClientRect();
        const popupHeight = 160;
        const spaceBelow = chatBodyRect.bottom - kebabRect.bottom;

        popup.classList.toggle('position-bottom', spaceBelow > popupHeight);
        popup.classList.toggle('position-top', spaceBelow <= popupHeight);
      }
    });

    this.isChatBodyUnscrollable = true; // chat body is unscrollable when message action popup is open

    // Observe changes to the messageElement's children
    observer.observe(messageElement, { childList: true, subtree: true });
  };

  deleteMessageForSelf(messageId) {
    const { isGroupcast, groupcastId } = getGroupcastUrlQuery();
    const memberId = this.userMetaData?.userId;
    if (isGroupcast && groupcastId) {
      deleteGroupcastMessageSelf({ groupcastId, messageId, memberId });
    } else {
      this.deleteForSelf({ conversationId: this.selectedConversationId, messageIds: messageId });
    }
    this.isDeletePopupOpen = null;
  }

  deleteMessageForEveryone(messageId) {
    const { isGroupcast, groupcastId } = getGroupcastUrlQuery();

    if (isGroupcast && groupcastId) {
      deleteGroupcastMessageEveryone({ groupcastId, messageId });
    } else {
      this.deleteForAll({ conversationId: this.selectedConversationId, messageIds: messageId });
    }
    this.isDeletePopupOpen = null;
  }

  async componentWillLoad() {
    const deviceId = await getDeviceID();
    this.deviceId = deviceId;
  }

  // Function to handle scroll event and trigger API call when scrolled near the bottom
  handleScroll = () => {
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    if (chatBodyDiv) {
      const scrollTop = chatBodyDiv.scrollTop;
      if (scrollTop === 0 && !this.isMessageLimitEnd) {
        this.updateSkip();
      }

      // Update showScrollButton based on scroll position
      this.showScrollButton = !this.isScrolledToBottom();

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.onScrollEnd();
      }, 300);
    }
  };

  onScrollEnd = () => {
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    const scrollTop = chatBodyDiv.scrollTop;
    this.previousTotalScrollHeight = chatBodyDiv.scrollHeight - scrollTop;
  };

  scrollToBottom = () => {
    requestAnimationFrame(() => {
      const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
      if (!chatBodyDiv) return;

      chatBodyDiv.scrollTop = chatBodyDiv.scrollHeight;

      this.showScrollButton = false;
      this.unreadMessageCount = 0;
      this.newMessageCountReset.emit();
    });
  };
  scrollToTop = () => {
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    if (!chatBodyDiv) return;

    requestAnimationFrame(() => {
      const toScroll = chatBodyDiv.scrollHeight - this.previousTotalScrollHeight;
      chatBodyDiv.scrollTop = toScroll;
      this.previousTotalScrollHeight = chatBodyDiv.scrollHeight;
    });
  };

  putReadAll = async () => {
    const hasUnreadMessages = this.allMessages?.some(message => (message?.senderId || message?.senderInfo?.userId) != this.userMetaData?.userId && !message?.readByAll);

    if (hasUnreadMessages) {
      await ChatSDK.getInstance().message.putRead({ conversationId: this.selectedConversationId });

      const newUpdatedAllMessages = this.allMessages?.map(message =>
        message?.readByAll != true && (message?.senderId || message?.senderInfo?.userId) != this.userMetaData?.userId ? { ...message, readByAll: true } : message,
      );
      this.updateAllMessages(newUpdatedAllMessages);

      this.el.dispatchEvent(
        new CustomEvent('message-read-status-updated', {
          bubbles: true,
          composed: true,
          detail: {
            conversationId: this.selectedConversationId,
            isRead: true,
            isDelivered: true,
          },
        }),
      );
    }
  };

  handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      this.isTabActive = true;
      this.putReadAll();
    } else {
      this.isTabActive = false;
    }
  };

  // Update the handleDocumentClick method to handle all popup types
  handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Close message action popup if clicking outside
    if (!target.closest('.messageActionPopup') && !target.closest('.kebab-menu')) {
      this.isMessageActionOpen = null;
      this.isChatBodyUnscrollable = false;
    }

    // Close delete popup if clicking outside
    if (!target.closest('.popup') && !target.closest('.messageActionPopup')) {
      this.isDeletePopupOpen = null;
    }
  };

  async handleReactionAdd(event: CustomEvent) {
    try {
      const { messageId, emojiId } = event.detail;

      await postReaction(emojiId, messageId, this.selectedConversationId);

      // Update the UI to show the reaction
      const updatedMessages = this.allMessages.map(message => {
        if (message.messageId === messageId) {
          return {
            ...message,
            reactions: [...(message.reactions || []), { type: emojiId, count: 1 }],
          };
        }
        return message;
      });

      this.updateAllMessages(updatedMessages);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  }
  async findAndScrollToMessage(messageId: string, isReplyClick: boolean = false) {
    let messageElement = this.el.shadowRoot.querySelector(`[data-message-id="${messageId}"]`);

    if (!messageElement && isReplyClick) {
      await this.updateSkip();

      messageElement = this.el.shadowRoot.querySelector(`[data-message-id="${messageId}"]`);

      let attempts = 0;
      const maxAttempts = 10;

      while (!messageElement && attempts < maxAttempts && !this.isMessageLimitEnd) {
        await this.updateSkip();
        messageElement = this.el.shadowRoot.querySelector(`[data-message-id="${messageId}"]`);
        attempts++;
      }
    }

    if (messageElement) {
      setTimeout(() => {
        this.scrollToMessage(messageId);
      }, 100);
    } else {
      console.log('Message not found after multiple attempts');
    }
  }
  // Attach the scroll listener after the component has loaded
  async componentDidLoad() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('click', this.handleDocumentClick);
    this.el.addEventListener('reaction-added', (event: CustomEvent) => {
      this.handleReactionAdd(event);
    });
    this.el.addEventListener('reaction-removed', (event: CustomEvent) => {
      this.handleReactionRemove(event);
    });
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    if (chatBodyDiv) {
      chatBodyDiv.addEventListener('scroll', this.handleScroll); // Listen to the scroll event
    }
    this.el.addEventListener('find-and-scroll-to-message', async event => {
      // @ts-ignore
      const { messageId, isReplyClick } = event.detail;
      await this.findAndScrollToMessage(messageId, isReplyClick);
    });
    this.scrollToBottom();
    this.putReadAll();
  }

  @Watch('allMessages')
  @Watch('userMetaData')
  async handleDataChange() {
    await new Promise(resolve => requestAnimationFrame(resolve));
    if (this.isScrollToBottom && this.allMessages?.length > 0 && this.userMetaData?.userId) {
      this.scrollToBottom();
      requestAnimationFrame(() => {
        this.changeScrollToBottomState(false);
      });
    } else if (this.allMessages?.length > 0 && this.userMetaData?.userId && this.isScrollToTop) {
      this.scrollToTop();
      requestAnimationFrame(() => {
        this.changeScrollToTopState(false);
      });
    }

    if (!this.isChatMinimized && !this.isChatClosed && this.isTabActive) {
      this.putReadAll();
    }
  }

  async handleReactionRemove(event: CustomEvent) {
    try {
      const { messageId, emojiId } = event.detail;

      await deleteReaction(this.selectedConversationId, messageId, emojiId);
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  }
  // Clean up the scroll listener when the component is removed
  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('click', this.handleDocumentClick);
    this.el.removeEventListener('reaction-added', (event: CustomEvent) => {
      this.handleReactionAdd(event);
    });
    this.el.removeEventListener('reaction-removed', (event: CustomEvent) => {
      this.handleReactionRemove(event);
    });
    const chatBodyDiv = this.el.shadowRoot.querySelector('.chat-body');
    if (chatBodyDiv) {
      chatBodyDiv.removeEventListener('scroll', this.handleScroll); // Remove the scroll listener
    }
  }

  openReplyBlock(messageId: string) {
    const message = this.allMessages?.find(msg => msg?.messageId === messageId);
    if (message) {
      this.replyMessageDetails.emit(message);
    }
    // this.isReplyBlockOpen = this.isReplyBlockOpen === messageId ? null : messageId;
  }

  closeReplyBlock() {
    this.isReplyBlockOpen = null;
  }

  closeMessageActionPopup() {
    this.isMessageActionOpen = null;
  }

  @Method()
  async scrollToMessage(messageId: string) {
    const messageElement = this.el.shadowRoot.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      messageElement.classList.add('highlighted');
      setTimeout(() => {
        messageElement.classList.remove('highlighted');
      }, 2000);
    }
  }

  getMessageReadStatus = async (messageId: string, conversationId: string) => {
    try {
      // Get read status
      const statusRead = await ChatSDK.getInstance().message.getStatusRead({
        conversationId: conversationId,
        messageId: messageId,
      });

      if (statusRead?.users && statusRead?.users?.length === 1) {
        this.messageReadTime = statusRead?.users[0]?.timestamp;
        this.readUserList = [statusRead?.users[0]];
      } else {
        this.readUserList = [...statusRead?.users];
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        this.messageReadTime = null;
        this.readUserList = [];
      }
      console.error('Error fetching read status', error);
    }
    // Separately get pending read count
    if (this.conversationDetails.isGroup) {
      try {
        const statusReadPending = await ChatSDK.getInstance().message.getStatusReadPending({
          conversationId: conversationId,
          messageId: messageId,
        });
        this.pendingReadCount = statusReadPending?.users.length - 1 || 0;
      } catch (error) {
        console.error('Error fetching pending read count', error);
        this.pendingReadCount = 0;
      }
    }
    return this.readUserList;
  };

  getMessageDeliveryStatus = async (messageId: string, conversationId: string, readUserIds: string[]) => {
    try {
      // Get delivery status
      const statusDelivered = await ChatSDK.getInstance().message.getStatusDelivery({
        conversationId: conversationId,
        messageId: messageId,
      });

      // Filter out users who have already read the message
      const unreadDeliveredUsers = statusDelivered?.users?.filter(user => !readUserIds.includes(user.userId));

      if (statusDelivered.users?.length === 1) {
        this.messageDeliveryTime = statusDelivered.users[0]?.timestamp;
        this.deliveryUserList = [statusDelivered.users[0]];
      } else {
        this.deliveryUserList = [...unreadDeliveredUsers];
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        this.messageDeliveryTime = null;
        this.deliveryUserList = [];
      }
      console.error('Error fetching delivery status', error);
    }
    if (this.conversationDetails.isGroup) {
      try {
        const statusDeliveryPending = await ChatSDK.getInstance().message.getStatusDeliveryPending({
          conversationId: conversationId,
          messageId: messageId,
        });
        this.pendingDeliveryCount = statusDeliveryPending?.users.length - 1 || 0;
      } catch (error) {
        console.error('Error fetching pending delivery count', error);
        this.pendingDeliveryCount = 0;
      }
    }
  };

  logMessageInfo = async () => {
   
    try {
      // Set initial loading state
      this.isLoadingInfo = true;

      // Reset states
      this.messageReadTime = 0;
      this.messageDeliveryTime = 0;
      this.messageInfoOpen = true;
      this.pendingReadCount = 0;
      this.pendingDeliveryCount = 0;

      const message = this.allMessages?.find(msg => msg?.messageId === this.isMessageActionOpen);
      this.messagePreviewText = message?.body;
      this.messageDate = formatMessageDateFromTimestamp(message?.sentAt);
      // Get read and delivery status for this specific message
      if (message) {

        const { isGroupcast, groupcastId } = await getGroupcastUrlQuery();
        
        if (isGroupcast && groupcastId) {
          // Handle groupcast message status
          const readUsers = await getGroupcastMessageReadStatus({ 
            messageId: message?.messageId, 
            groupcastId: groupcastId 
          });
          this.readUserList = readUsers;
  
          const deliveryStatus = await getGroupcastMessageDeliveryStatus({ 
            messageId: message?.messageId, 
            groupcastId: groupcastId 
          });
          this.deliveryUserList = deliveryStatus?.users || [];
        } else {
          // Handle regular conversation message status
          const readUsers = await this.getMessageReadStatus(message?.messageId, message?.conversationId);
          const readUserIds = readUsers.map(user => user.userId);
          await this.getMessageDeliveryStatus(message?.messageId, message?.conversationId, readUserIds);
        }
      
        this.selectedMessage = message;
      } 
    } catch (error) {
      console.error('Error loading message info:', error);
    } finally {
      // Always set loading state to false when done
      this.isLoadingInfo = false;
    }
  };
  updateMessageStatus(messageId: string, conversationId: string, isRead: boolean, isDelivered: boolean) {
    if (isRead) {
      this.el.dispatchEvent(
        new CustomEvent('conversation-unread-count-changed', {
          bubbles: true,
          composed: true,
        }),
      );
    }
    // Update local message state
    this.allMessages = this.allMessages.map(msg => {
      if (msg.messageId === messageId) {
        return {
          ...msg,
          readByAll: isRead,
          deliveredToAll: isDelivered,
        };
      }
      return msg;
    });

    // Emit event for sidebar to update
    this.messageReadStatusUpdated.emit({
      messageId,
      conversationId,
      isRead,
      isDelivered,
    });
  }
  renderAttachmentMessage = (message: any, isSelf: boolean) => {
    // Common props for all message types
    const commonOutgoingProps = {
      time: message?.sentAt,
      isSelf: isSelf,
      isRead: message?.readByAll,
      isDelivered: `${message?.deliveredToAll}`,
      textColor: '#FFFFFF',
      timeColor: '#FFFFFF',
      message: message?.body,
      mentionedUsers: message?.mentionedUsers,
      bgColor: '#241172',
      borderRadius: '6px 6px 0 6px',
      messageType: message.messageType,
    };

    const commonIncomingProps = {
      time: message?.sentAt,
      isSelf: isSelf,
      textColor: 'var(--text-incoming-message)',
      timeColor: 'var(--text-incoming-time)',
      message: message?.body,
      mentionedUsers: message?.mentionedUsers,
      bgColor: 'var(--bg-grey-color)',
      borderRadius: '6px 6px 6px 0',
      messageId: message?.messageId,
      reactions: message?.reactions,
      messageType: message.messageType,
      userMetaData: this.userMetaData,
    };

    const props = isSelf ? commonOutgoingProps : commonIncomingProps;

    if (message?.attachments?.length > 1 && !message?.attachments?.[0]?.mimeType?.startsWith('application/')) {
      return (
        <image-message
          {...props}
          height={'220px'}
          width={'250px'}
          padding="10px 10px 10px 10px"
          imageSrc={message?.attachments?.[0]?.mediaUrl}
          borderWidth={'5px'}
          borderColor={isSelf ? 'var(--bg-grey-color)' : '#F2F2F2'}
          borderStyle={'solid'}
          allAttachments={this.allAttachments}
          onImageClick={this.onMediaClick}
          totalMedia={message?.attachments?.length - 1}
          mediaType={message.attachments?.[0]?.mediaType}
          loadingMediaGallery={this.loadingMediaGallery}
        ></image-message>
      );
    } else {
      return message?.attachments?.map(attachment => {
        if (attachment?.mimeType?.startsWith('application/') || message?.customType === 'AttachmentMessage:File') {
          return (
            <document-message
              {...props}
              docType={attachment?.mimeType}
              fileName={attachment?.name}
              href={attachment?.mediaUrl}
              width="220px"
              padding="10px 10px 40px 10px"
              docName={attachment?.name}
            ></document-message>
          );
        }

        if (attachment?.mimeType?.startsWith('video/') || message?.customType === 'AttachmentMessage:Video') {
          return (
            <video-message {...props} height={'220px'} width={'250px'} padding="10px 10px 30px 10px" type={attachment?.mimeType} videoSrc={attachment?.mediaUrl}></video-message>
          );
        }

        if (attachment?.mimeType?.startsWith('image/') || message?.customType === 'AttachmentMessage:Image') {
          return (
            <image-message
              {...props}
              height={'220px'}
              width={'250px'}
              padding="10px 10px 10px 10px"
              imageSrc={attachment?.mediaUrl}
              borderWidth={'5px'}
              borderColor={isSelf ? 'var(--bg-grey-color)' : '#F2F2F2'}
              borderStyle={'solid'}
              allAttachments={this.allAttachments}
              onImageClick={this.onMediaClick}
              loadingMediaGallery={this.loadingMediaGallery}
            ></image-message>
          );
        }
        if (attachment?.mimeType?.startsWith('audio/') || message?.customType === 'AttachmentMessage:Image') {
          return <audio-bubble {...props} isRead={message.readByAll} isDelivered={message.deliveredToAll} audioUrl={attachment?.mediaUrl} showFooter></audio-bubble>;
        }
      });
    }
  };
  async closeMessageInfo() {
    this.messageInfoOpen = false;
    this.readUserList = [];
    this.deliveryUserList = [];
    this.pendingReadCount = 0;
    this.pendingDeliveryCount = 0;
  }
  @Watch('selectedConversationId')
  async handleChnageChangeConversationId() {
    this.closeMessageInfo();
  }
  renderMessageAction(message: any, isSelf: boolean, position: string) {
    const actionButtons = [
      {
        icon: (
          <svg width="14px" height="14px" viewBox="-0.5 0 28 28" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="var(--popup-label-color)">
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g id="Icon-Set" transform="translate(-100.000000, -1193.000000)" fill="var(--popup-label-color)">
                  <path
                    d="M113,1208 C112.346,1208 109.98,1208.02 109.98,1208.02 L109.98,1213.39 L102.323,1205 L109.98,1196.6 L109.98,1202.01 C109.98,1202.01 112.48,1201.98 113,1202 C120.062,1202.22 124.966,1210.26 124.998,1214.02 C122.84,1211.25 117.17,1208 113,1208 L113,1208 Z M111.983,1200.01 L111.983,1194.11 C112.017,1193.81 111.936,1193.51 111.708,1193.28 C111.312,1192.89 110.67,1192.89 110.274,1193.28 L100.285,1204.24 C100.074,1204.45 99.984,1204.72 99.998,1205 C99.984,1205.27 100.074,1205.55 100.285,1205.76 L110.219,1216.65 C110.403,1216.88 110.67,1217.03 110.981,1217.03 C111.265,1217.03 111.518,1216.91 111.7,1216.72 C111.702,1216.72 111.706,1216.72 111.708,1216.71 C111.936,1216.49 112.017,1216.18 111.983,1215.89 C111.983,1215.89 112,1210.34 112,1210 C118.6,1210 124.569,1214.75 125.754,1221.01 C126.552,1219.17 127,1217.15 127,1215.02 C127,1206.73 120.276,1200.01 111.983,1200.01 L111.983,1200.01 Z"
                    id="reply"
                  ></path>
                </g>
              </g>
            </g>
          </svg>
        ),
        label: WEB_CONTENT.MESSAGE_ACTIONS.REPLY,
        onClick: () => {
          this.openReplyBlock(message.messageId);
          this.closeMessageActionPopup();
        },
      },
     
      {
        icon: (
          <svg fill="var(--popup-label-color)" width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.755,20.283,4,8H20L18.245,20.283A2,2,0,0,1,16.265,22H7.735A2,2,0,0,1,5.755,20.283ZM21,4H16V3a1,1,0,0,0-1-1H9A1,1,0,0,0,8,3V4H3A1,1,0,0,0,3,6H21a1,1,0,0,0,0-2Z"></path>
          </svg>
        ),
        label: WEB_CONTENT.MESSAGE_ACTIONS.DELETE,
        onClick: e => {
          this.openDeletePopup(e, message?.messageId, isSelf);
          this.closeMessageActionPopup();
        },
      },
    ];

    const selfOnlyButtons = [
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="var(--popup-label-color)">
            <path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-640q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-640Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
          </svg>
        ),
        label: WEB_CONTENT.MESSAGE_ACTIONS.INFO,
        onClick: () => {
          this.logMessageInfo();
          this.closeMessageActionPopup();
        },
      },
    ];

    return (
      <div class={`messageActionPopup ${position === 'top' ? 'position-top' : ''}`}>
        {actionButtons.map((button, index) => (
          <button key={index} onClick={button.onClick}>
            {button.icon}
            {button.label}
          </button>
        ))}
        {isSelf &&
          selfOnlyButtons.map((button, index) => (
            <button key={`self-${index}`} onClick={button.onClick}>
              {button.icon}
              {button.label}
            </button>
          ))}
      </div>
    );
  }
  render() {
    const currentConversation = this.conversationList?.find(conversation => conversation?.conversationId === this.selectedConversationId);

    const shouldShowPlaceholder =
      this.allMessages?.length === 1 && this.allMessages?.[0]?.action !== 'clearConversation' && this.allMessages?.some(message => message?.action === 'clearConversation');

    return (
      <div
   
        class={`chat-body ${this.isChatBodyUnscrollable ? 'unscrollable' : null}`}
      >
        {this.showScrollButton && (
          <div
            onClick={this.scrollToBottom}
            style={{
              position: 'sticky',
              top: '90%',
              left: '95%',
              backgroundColor: 'var(--bg-secondary-color)',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
              zIndex: '100',
            }}
          >
            {this.newMessageNotification?.count > 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '-4px',
                  backgroundColor: 'var(--text-primary-color)',
                  color: 'var(--bg-primary-color)',
                  borderRadius: '50%',
                  width: '11px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  padding: '0 4px',
                }}
              >
                {this.newMessageNotification?.count > 9 ? '9+' : this.newMessageNotification?.count}
              </div>
            )}
            <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M12 19L19 12M12 19L5 12" stroke="var(--text-primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        )}
        {shouldShowPlaceholder && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              justifyContent: 'center',
              gap: '20px',
              textAlign: 'center',
              color: 'var(--text-secondary-color)',
              marginTop: '20px',
            }}
          >
            <img src={NoMessagePlaceholder} alt="No messages" style={{ width: '200px', height: '200px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px', color: 'var(--text-primary-color)' }}>No messages</span>
              <span style={{ fontSize: '16px', color: 'var(--text-secondary-color)' }}>Send a "Hi" and make someone's day brighter!</span>
            </div>
          </div>
        )}
        {this.isMessagesLoading && (
          <spinner-loader
            height="20px"
            width="20px"
            spinTime="1s"
            spinnerPrimaryColor="#000"
            spinnerSecondaryColor="var(--bg-grey-color)"
            spinnerBorderWidth="3px"
          ></spinner-loader>
        )}

        {this.messageInfoOpen && (
          <div class="message-info-container">
            {this.isLoadingInfo ? (
              <div class="spinner-wrapper">
                <spinner-loader
                  height="15px"
                  width="15px"
                  spinTime="1s"
                  spinnerPrimaryColor="var(--bg-primary-color)"
                  spinnerSecondaryColor="var(--text-primary-color)"
                  spinnerBorderWidth="2px"
                ></spinner-loader>
              </div>
            ) : (
              <message-info
                height="100%"
                width="100%"
                padding="10px"
                backgroundColor="var(--bg-primary-color)"
                messageBackgroundColor="var(--bg-secondary-color)"
                statusBackgroundColor="var(--bg-secondary-color)"
                selectedMessage={this.selectedMessage}
                onMediaClick={this.onMediaClick}
                allAttachments={this.allAttachments}
                messageDeliveryTime={this.messageDeliveryTime}
                messageReadTime={this.messageReadTime}
                readUserList={this.readUserList}
                deliveryUserList={this.deliveryUserList}
                isGroup={currentConversation?.isGroup}
                pendingReadCount={this.pendingReadCount}
                pendingDeliveryCount={this.pendingDeliveryCount}
                closePopup={() => this.closeMessageInfo()}
                fontSize="16px"
              ></message-info>
            )}
          </div>
        )}

        {this.allMessages?.length > 0 &&
          this.userMetaData?.userId &&
          this.allMessages?.map((message: any, index: number) => {
            const currentMessageDate = formatMessageDateFromTimestamp(this.allMessages?.[index]?.sentAt);
            const previousMessageDate = formatMessageDateFromTimestamp(this.allMessages?.[index - 1]?.sentAt || '');
            const showDatePill = currentMessageDate !== previousMessageDate;
            let isSelf = (message?.senderId || message?.senderInfo?.userId) === this.userMetaData?.userId;

            if (message.messageType === 2) {
              return (
                <div
                  style={{
                    position: 'relative',
                    flexGrow: '1 1 100%',
                    display: 'flex',
                    justifyContent: isSelf ? 'end' : 'start',
                  }}
                  data-message-id={message?.messageId}
                >
                  <div id="reply-message-container" style={{ display: 'flex', gap: '4px', maxWidth: '88%' }}>
                    {currentConversation?.isGroup && (message?.senderId || message?.senderInfo?.userId) !== this.userMetaData?.userId && (
                      <div style={{ width: 'auto' }}>
                        <img
                          src={message.senderInfo?.userProfileImageUrl || message.senderProfileImageUrl}
                          alt="Sender"
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                          }}
                          onError={e => {
                            const target = e.target as HTMLImageElement;
                            target.src = ProfilePlaceholder;
                          }}
                        />
                      </div>
                    )}
                    <reply-message-container
                      parentMessageId={message?.parentMessageId}
                      messageBody={message?.body}
                      parentmessageBody={message?.metaData?.parentMessage}
                      senderName={message.senderInfo?.userName}
                      parentMessageSenderName={message?.metaData?.parentMessageSenderName}
                      messageTime={message?.sentAt}
                      parentMessageSenderIdentifier={message?.metaData?.parentMessageSenderId}
                      parentMessageTime={formatUnixTimestamp(message?.metaData?.parentMessageTime)}
                      repliedMessage={message?.body}
                      isRead={message?.readByAll}
                      isDelivered={message?.deliveredToAll}
                      userMetaData={this.userMetaData}
                      messageInfo={message}
                      backgroundColor={isSelf ? 'var(--bg-outgoing-message)' : 'var(--bg-incoming-message)'}
                      color={isSelf ? 'var(--text-outgoing-message)' : 'var(--text-incoming-message)'}
                      timeColor={isSelf ? 'var(--text-outgoing-time)' : 'var(--text-incoming-time)'}
                      parentMessageMediaUrl={message?.metaData?.parentMessageMediaUrl}
                      parentMessageType={message?.metaData?.parentMessageType}
                      isSelf={isSelf}
                      messageId={message?.messageId}
                      reactions={message?.reactions}
                    />

                    <span class={`kebab-menu ${isSelf ? 'kebab-menu-right' : 'kebab-menu-left'}`} onClick={e => this.openMessageActionsPopup(e, message?.messageId)}>
                      <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                          <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                        </g>
                      </svg>
                    </span>
                  </div>

                  {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, 'top') : null}
                </div>
              );
            }

            if (message.attachments) {
              return (
                <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '15px' }} data-message-id={message.messageId}>
                  {showDatePill && (
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                      <message-date-pill
                        textColor={'var(--text-secondary-color)'}
                        borderRadius={'9px'}
                        bgColor={'transparent'}
                        padding={'5px 20px'}
                        fontSize={'15px'}
                        fontWeight={'500'}
                        content={currentMessageDate}
                      ></message-date-pill>
                    </div>
                  )}
                  {/* outgoing messages */}
                  <div
                    id={isSelf ? 'outgoing-message' : 'incoming-message'}
                    style={{ position: 'relative', display: 'flex', width: '100%', justifyContent: isSelf ? 'right' : 'left' }}
                    data-message-id={message.messageId}
                  >
                    {currentConversation?.isGroup && !isSelf && (
                      <div style={{ width: 'auto' }}>
                        <img
                          src={message.senderInfo?.userProfileImageUrl || message.senderProfileImageUrl}
                          alt="Sender"
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '10px',
                          }}
                          onError={e => {
                            const target = e.target as HTMLImageElement;
                            target.src = ProfilePlaceholder;
                          }}
                        />
                      </div>
                    )}
                    {this.renderAttachmentMessage(message, isSelf)}
                    <span class={`kebab-menu ${isSelf ? 'kebab-menu-right' : 'kebab-menu-left'}`} onClick={e => this.openMessageActionsPopup(e, message?.messageId)}>
                      <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                          <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                        </g>
                      </svg>
                    </span>

                    {/* message action popup for outgoing message attachments  */}
                    {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, 'top') : null}
                  </div>
                </div>
              );
            } else if (message.body) {
              const urls = this.extractUrls(message.body);
              const hasLinks = urls.length > 0;

              if (hasLinks) {
                // For outgoing messages with links
                if (isSelf) {
                  return (
                    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '15px' }} data-message-id={message.messageId}>
                      {showDatePill && (
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                          <message-date-pill
                            textColor={'var(--text-secondary-color)'}
                            borderRadius={'9px'}
                            bgColor={'transparent'}
                            padding={'5px 20px'}
                            fontSize={'15px'}
                            content={currentMessageDate}
                            fontWeight={'500'}
                          ></message-date-pill>
                        </div>
                      )}
                      <div id="outgoing-message" style={{ position: 'relative', display: 'flex', width: '100%', justifyContent: 'end' }}>
                        <link-preview
                          url={urls[0]}
                          isRead={message.readByAll}
                          isDelivered={message.deliveredToAll}
                          time={message.sentAt}
                          textColor="var(--text-outgoing-message)"
                          backgroundColor="var(--bg-outgoing-message)"
                          message={message.body}
                          isSelf={true}
                        />
                        <span
                          class="kebab-menu"
                          style={{ cursor: 'pointer', position: 'absolute', right: '-12px ', top: '5px' }}
                          onClick={e => this.openMessageActionsPopup(e, message?.messageId)}
                        >
                          <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                              <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                            </g>
                          </svg>
                        </span>
                        {/* baards */}
                        {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, null) : null}
                      </div>
                    </div>
                  );
                } else {
                  // For incoming messages with links
                  return (
                    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '15px' }} data-message-id={message.messageId}>
                      {showDatePill && (
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                          <message-date-pill
                            textColor={'var(--text-secondary-color)'}
                            borderRadius={'9px'}
                            bgColor={'transparent'}
                            padding={'5px 20px'}
                            fontSize={'15px'}
                            content={currentMessageDate}
                          ></message-date-pill>
                        </div>
                      )}
                      <div style={currentConversation?.isGroup ? { display: 'flex' } : {}} id="incoming-message">
                        {currentConversation?.isGroup && (
                          <div style={{ width: 'auto' }}>
                            <img
                              src={message.senderInfo?.userProfileImageUrl || message.senderProfileImageUrl}
                              alt="Sender"
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                marginRight: '10px',
                              }}
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                target.src = ProfilePlaceholder;
                              }}
                            />
                          </div>
                        )}
                        <link-preview
                          url={urls[0]}
                          isRead={message.readByAll}
                          isDelivered={message.deliveredToAll}
                          time={message.sentAt}
                          message={message.body}
                          isSelf={false}
                          textColor="var(--text-incoming-message)"
                          backgroundColor="var(--bg-incoming-message)"
                        />
                        <span
                          class="kebab-menu"
                          style={{ cursor: 'pointer', position: 'absolute', left: '-12px ', top: '5px' }}
                          onClick={e => this.openMessageActionsPopup(e, message?.messageId)}
                        >
                          <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                              <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                            </g>
                          </svg>
                        </span>
                        {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, 'top') : null}
                      </div>
                    </div>
                  );
                }
              } else {
                // Existing code for regular text messages
                if (isSelf) {
                  return (
                    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '15px' }} data-message-id={message.messageId}>
                      {showDatePill && (
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                          <message-date-pill
                            textColor={'var(--text-secondary-color)'}
                            borderRadius={'9px'}
                            bgColor={'transparent'}
                            padding={'5px 20px'}
                            fontSize={'15px'}
                            fontWeight={'500'}
                            content={currentMessageDate}
                          ></message-date-pill>
                        </div>
                      )}
                      <div id="outgoing-message" style={{ position: 'relative', display: 'flex', width: '100%', justifyContent: 'end' }} data-message-id={message.messageId}>
                        <outgoing-text-message-bubble
                          isRead={message.readByAll}
                          padding="10px 10px 10px 10px"
                          borderRadius="10px 10px 0 10px"
                          textContent={message?.body}
                          time={message?.sentAt}
                          bgColor="#241172"
                          textColor="#FFFFFF"
                          isDelivered={`${message?.deliveredToAll}`}
                          customType={message?.customType}
                          mentionedUsers={message?.mentionedUsers}
                          messageId={message?.messageId}
                          reactions={message?.reactions}
                          messageType={message?.messageType}
                          timeColor="#FFFFFF"
                        />

                        <span class={`kebab-menu ${isSelf ? 'kebab-menu-right' : 'kebab-menu-left'}`} onClick={e => this.openMessageActionsPopup(e, message?.messageId)}>
                          <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                              <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                            </g>
                          </svg>
                        </span>
                        {/* baards */}
                        {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, 'top') : null}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '15px' }} data-message-id={message.messageId}>
                      {showDatePill && (
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                          <message-date-pill
                            textColor={'var(--text-secondary-color)'}
                            borderRadius={'9px'}
                            bgColor={'transparent'}
                            padding={'5px 20px'}
                            fontSize={'15px'}
                            fontWeight={'500'}
                            content={currentMessageDate}
                          ></message-date-pill>
                        </div>
                      )}
                      <div style={currentConversation?.isGroup ? { display: 'flex' } : {}} id="incoming-message">
                        {currentConversation?.isGroup && (
                          <div style={{ width: 'auto' }}>
                            <img
                              src={message.senderInfo?.userProfileImageUrl || message.senderProfileImageUrl}
                              alt="Sender"
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                marginRight: '10px',
                              }}
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                target.src = ProfilePlaceholder;
                              }}
                            />
                          </div>
                        )}
                        <incoming-text-message-bubble
                          color="var(--text-incoming-message)"
                          bgColor="var(--bg-grey-color)"
                          timeColor="var(--text-incoming-time)"
                          borderRadius="10px 10px 10px 0"
                          time={message?.sentAt}
                          textContent={message?.body}
                          customType={message?.customType}
                          mentionedUsers={message?.mentionedUsers}
                          messageId={message?.messageId}
                          reactions={message?.reactions}
                          padding="10px 10px 20px 10px"
                          userMetaData={this.userMetaData}
                          messageType={message?.messageType}
                        />

                        <span class={`kebab-menu ${isSelf ? 'kebab-menu-right' : 'kebab-menu-left'}`} onClick={e => this.openMessageActionsPopup(e, message?.messageId)}>
                          <svg width="14px" height="14px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="var(--text-primary-color)" class="bi bi-three-dots-vertical">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                              <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                            </g>
                          </svg>
                        </span>
                        {this.isMessageActionOpen === message?.messageId ? this.renderMessageAction(message, isSelf, 'top') : null}
                      </div>
                    </div>
                  );
                }
              }
            } else if (
              message.action == 'conversationCreated' ||
              message.action == 'clearConversation' ||
              message.action == 'deleteConversationLocally' ||
              message.action == 'membersAdd' ||
              message.action == 'membersRemove' ||
              message.action == 'addAdmin' ||
              message.action == 'removeAdmin' ||
              message.action == 'userBlockConversation' ||
              message.action == 'userUnblockConversation' ||
              message.action == 'reactionAdd'
            ) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  {showDatePill && (
                    <message-date-pill
                      textColor={'var(--text-secondary-color)'}
                      borderRadius={'9px'}
                      bgColor={'transparent'}
                      padding={'5px 20px'}
                      fontSize={'15px'}
                      fontWeight={'500'}
                      content={currentMessageDate}
                    ></message-date-pill>
                  )}
                
                  {message.action == 'clearConversation' && (
                    <div class="messagePillContainer">Conversation Cleared by {this.userMetaData?.userId === message.userId ? 'You' : message.userName}</div>
                  )}

                  {message.action === 'membersAdd' && (
                    <div class="messagePillContainer">
                      {message?.userId === this.userMetaData?.userId ? 'You' : message?.userName} added {message.members.map(member => member.memberName).join(', ')}
                    </div>
                  )}
                  {message.action === 'reactionAdd' && (
                    <div class="messagePillContainer">
                      {message.userId === this.userMetaData?.userId
                        ? `You reacted ${this.getReactionEmoji(message.reactionType)} to a message`
                        : `${message.userName} ${this.getReactionEmoji(message.reactionType)} reacted to a message`}
                    </div>
                  )}
                  {message.action === 'userUnblockConversation' && (
                    <div class="messagePillContainer">
                      {message.initiatorId === this.userMetaData?.userId ? 'You unblocked' + ' ' + message.opponentName : message.initiatorName + ' ' + 'unblocked you'}
                    </div>
                  )}
                  {message.action === 'userBlockConversation' && (
                    <div class="messagePillContainer">
                      {message.initiatorId === this.userMetaData?.userId ? '🚫 You blocked' + ' ' + message.opponentName : '🚫' + message.initiatorName + ' ' + 'blocked you'}`
                    </div>
                  )}
                  {message.action === 'membersRemove' && (
                    <div class="messagePillContainer">
                      {message?.userId === this.userMetaData?.userId ? 'You' : message?.userName} removed {message.members.map(member => member.memberName).join(', ')}
                    </div>
                  )}
                  {message.action === 'addAdmin' && (
                    <div class="messagePillContainer">
                      {message?.initiatorId === this.userMetaData?.userId ? 'You' : message?.initiatorName} made {message.memberName} an admin
                    </div>
                  )}

                  {message.action === 'removeAdmin' && (
                    <div class="messagePillContainer">
                      {message?.initiatorId === this.userMetaData?.userId ? 'You' : message?.initiatorName} removed {message.memberName} as an admin
                    </div>
                  )}
                </div>
              );
            } else if (message.action == 'conversationTitleUpdated') {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }} data-message-id={message?.messageId}>
                  {showDatePill && currentMessageDate && (
                    <message-date-pill
                      textColor={'var(--text-secondary-color)'}
                      borderRadius={'9px'}
                      bgColor={'transparent'}
                      padding={'5px 20px'}
                      content={currentMessageDate}
                      fontSize={'15px'}
                      fontWeight={'500'}
                    ></message-date-pill>
                  )}
                  <div class="messagePillContainer">
                    {this.userMetaData?.userId === message.userId ? 'You' : message?.userName} updated the conversation title to {message.conversationTitle}
                  </div>
                </div>
              );
            } else if (message.action == 'memberLeave') {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  {showDatePill && (
                    <message-date-pill
                      textColor={'var(--text-secondary-color)'}
                      borderRadius={'9px'}
                      bgColor={'transparent'}
                      padding={'5px 20px'}
                      fontSize={'15px'}
                      fontWeight={'500'}
                      content={currentMessageDate}
                    ></message-date-pill>
                  )}
                  <div class="messagePillContainer">{this.userMetaData?.userId === message.userId ? 'You' : message.userName} left the conversation</div>
                </div>
              );
            } else if (message.action == 'memberRemove') {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  {showDatePill && (
                    <message-date-pill
                      textColor={'var(--text-secondary-color)'}
                      borderRadius={'9px'}
                      bgColor={'transparent'}
                      padding={'5px 20px'}
                      fontSize={'15px'}
                      fontWeight={'500'}
                      content={currentMessageDate}
                    ></message-date-pill>
                  )}
                  <div class="messagePillContainer">
                    {message.userName} removed {message.members.map(member => member.memberName).join(', ')}
                  </div>
                </div>
              );
            }
          })}
        {/* {this.isMessageSending && <h3>Uploading...</h3>} */}
      </div>
    );
  }
}

export { WebChatBody };
